/**
 * **Past raves work online only** (the user, 2026-11-02, the QA walk: "past raves work only online — keep the rave
 * offline for 2 days until it's over and then remove it from the phone").
 *
 * A rave's offline copy lives until two days after it ends (`KEEP_AFTER_END_MS`, the same rule `raveSlots.ts` uses
 * for the app itself). After that, on this address: the stored lineup is never served (`bundle.ts` `ownStored`), a
 * new one is never stored, its photos and map are never downloaded (`OfflineProvider`), and what is already on the
 * phone is deleted — the lineup, the download's progress and its photo caches (`forgetPastRaves`, run on every rave
 * page by `RaveOffline`). Opening it with no signal says it is over and needs a connection (`EditionProvider`).
 */
import { db } from './db';
import { readJson, writeJson } from '@/lib/storage';

import { overForOffline } from './pastRaveRule';

export { KEEP_AFTER_END_MS, overForOffline } from './pastRaveRule';

/** How often a page looks for past copies to delete — reading every stored lineup is not free. */
export const FORGET_EVERY_MS = 6 * 60 * 60_000;
const ASSET_PREFIX = 'myrave-assets|';

/**
 * Delete every rave copy on this address that is past its offline days: the lineup, the download's progress and
 * the photo caches. Returns the edition ids it removed. At most once per `FORGET_EVERY_MS` unless `force`.
 */
export async function forgetPastRaves(now = Date.now(), force = false): Promise<string[]> {
  if (!force) {
    const last = readJson<number | null>(null, 'past-raves-checked', null);
    if (last !== null && now - last < FORGET_EVERY_MS && now >= last) return [];
  }
  writeJson(null, 'past-raves-checked', now);
  const gone: string[] = [];
  try {
    await db.bundles.each((b) => { if (overForOffline(b.bundle?.edition?.endsAt, now)) gone.push(b.editionId); });
    if (gone.length) {
      await db.bundles.bulkDelete(gone);
      await db.assetProgress.bulkDelete(gone);
    }
  } catch {
    // No storage (a private window, blocked site data): nothing is kept, nothing to delete.
  }
  if (gone.length && typeof caches !== 'undefined') {
    const names = await caches.keys().catch(() => [] as string[]);
    for (const name of names) {
      if (name.startsWith(ASSET_PREFIX) && gone.some((id) => name.startsWith(`${ASSET_PREFIX}${id}|`))) {
        await caches.delete(name).catch(() => false);
      }
    }
  }
  return gone;
}
